<script setup lang="ts">
import { CompanyProjectProperty, PropertyDataType } from '@beeboard/common';
import { useCustomPropertiesStore } from '@/store/customPropertiesStore';

interface Props {
  modelValue: Record<string, unknown>;
  projectId?: string | null;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  projectId: null,
  loading: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown>];
  'update:hasValidationErrors': [value: boolean];
}>();

const alerts = useAlerts();
const customPropertiesStore = useCustomPropertiesStore();
const properties = computed(
  () => customPropertiesStore.companyProjectProperties,
);
const loadingProperties = ref(true);
const formValues = ref<Record<string, unknown>>({});
const isUpdatingFromProp = ref(false);

// Computed property to check if there are any validation errors
const hasValidationErrors = computed(() => {
  return properties.value.some((property) => {
    const error = validateField(property, getFieldValue(property));

    return error !== undefined;
  });
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue) {
      isUpdatingFromProp.value = true;
      formValues.value = { ...newValue };
      nextTick(() => {
        isUpdatingFromProp.value = false;
      });
    }
  },
  { immediate: true },
);

watch(
  formValues,
  (newValues) => {
    if (!isUpdatingFromProp.value) {
      emit('update:modelValue', { ...newValues });
    }
  },
  { deep: true },
);

watch(
  hasValidationErrors,
  (hasErrors) => {
    emit('update:hasValidationErrors', hasErrors);
  },
  { immediate: true },
);

watch(properties, (newValues) => {
  // Return if project is old
  if (props.projectId) {
    return;
  }
  // Initialize form values with default values for new projects
  newValues.forEach((property) => {
    if (
      property.default_value !== null &&
      property.default_value !== undefined &&
      formValues.value[property.key] === undefined
    ) {
      formValues.value[property.key] = parseDefaultValue(
        property.default_value,
        property.data_type,
      );
    }
  });
});

onMounted(async () => {
  await loadProperties();
});

async function loadProperties() {
  loadingProperties.value = true;
  try {
    await customPropertiesStore.loadCompanyProjectProperties();
  } catch (error) {
    alerts.error(error);
  } finally {
    loadingProperties.value = false;
  }
}

function parseDefaultValue(
  value: string,
  data_type: PropertyDataType,
): string | number | boolean | string[] {
  switch (data_type) {
    case 'number':
      return parseFloat(value);
    case 'boolean':
      return value === 'true';
    case 'multiselect':
      try {
        return JSON.parse(value) as string[];
      } catch {
        return [];
      }
    default:
      return value;
  }
}

function getFieldValue(property: CompanyProjectProperty): unknown {
  const value = formValues.value[property.key];

  if (value === undefined || value === null) {
    switch (property.data_type) {
      case 'boolean':
        return false;
      case 'multiselect':
        return [];
      default:
        return null;
    }
  }

  return value;
}

function getTextFieldValue(property: CompanyProjectProperty): string | null {
  return getFieldValue(property) as string | null;
}

function getNumberFieldValue(property: CompanyProjectProperty): number | null {
  return getFieldValue(property) as number | null;
}

function getDateFieldValue(property: CompanyProjectProperty): string | null {
  return getFieldValue(property) as string | null;
}

function getBooleanFieldValue(property: CompanyProjectProperty): boolean {
  return getFieldValue(property) as boolean;
}

function getSelectFieldValue(property: CompanyProjectProperty): string | null {
  return getFieldValue(property) as string | null;
}

function getMultiSelectFieldValue(property: CompanyProjectProperty): string[] {
  const value = getFieldValue(property);
  if (Array.isArray(value)) {
    return value;
  }

  return [];
}

function updateFieldValue(
  property: CompanyProjectProperty,
  value: unknown,
): void {
  // Convert value based on data type
  switch (property.data_type) {
    case 'number':
      formValues.value[property.key] =
        value !== '' ? parseFloat(value as string) : null;
      break;
    case 'boolean':
      formValues.value[property.key] = !!value;
      break;
    case 'date':
      formValues.value[property.key] = value || null;
      break;
    default:
      formValues.value[property.key] = value;
  }
}

function getSelectOptions(
  property: CompanyProjectProperty,
): Array<{ label: string; value: string }> {
  const options = property.validation_rules?.options || [];

  // Transform strings to the format expected by UI components
  return options.map((option) =>
    typeof option === 'string' ? { label: option, value: option } : option,
  );
}

function isFieldRequired(property: CompanyProjectProperty): boolean {
  return property.required;
}

function validateField(
  property: CompanyProjectProperty,
  value: unknown,
): string | undefined {
  if (
    property.required &&
    (value === null || value === undefined || value === '')
  ) {
    return $t('validation.required');
  }

  if (
    property.validation_rules &&
    value !== null &&
    value !== undefined &&
    value !== ''
  ) {
    const rules = property.validation_rules;

    switch (property.data_type) {
      case 'text': {
        const textValue = value as string;
        if (rules.minLength && textValue.length < rules.minLength) {
          return $t('validation.minLength', { min: rules.minLength });
        }
        if (rules.maxLength && textValue.length > rules.maxLength) {
          return $t('validation.maxLength', { max: rules.maxLength });
        }
        if (rules.pattern && !new RegExp(rules.pattern).test(textValue)) {
          return $t('validation.pattern');
        }
        break;
      }

      case 'number': {
        const numValue = parseFloat(value as string);
        if (rules.min !== undefined && numValue < rules.min) {
          return $t('validation.minValue', { min: rules.min });
        }
        if (rules.max !== undefined && numValue > rules.max) {
          return $t('validation.maxValue', { max: rules.max });
        }
        break;
      }
    }
  }

  return undefined;
}

function getHint(property: CompanyProjectProperty): string | undefined {
  return property.description ?? undefined;
}
</script>

<template>
  <div class="project-custom-properties">
    <div v-if="loadingProperties" class="loading">
      <UiSpinner />
    </div>

    <div v-else-if="properties.length === 0" class="no-properties">
      <!-- No custom properties defined -->
    </div>

    <div v-else class="properties-fields">
      <div
        v-for="property in properties"
        :key="property.id"
        class="property-field"
      >
        <!-- Text field -->
        <UiInput
          v-if="property.data_type === 'text'"
          :model-value="getTextFieldValue(property)"
          :label="property.name"
          :hint="getHint(property)"
          :required="isFieldRequired(property)"
          :disabled="props.loading"
          :error="validateField(property, getFieldValue(property))"
          @update:model-value="updateFieldValue(property, $event)"
        />

        <!-- Number field -->
        <UiInput
          v-else-if="property.data_type === 'number'"
          :model-value="getNumberFieldValue(property)"
          type="number"
          :label="property.name"
          :hint="getHint(property)"
          :required="isFieldRequired(property)"
          :disabled="props.loading"
          :error="validateField(property, getFieldValue(property))"
          @update:model-value="updateFieldValue(property, $event)"
        />

        <!-- Date field -->
        <UiField
          v-else-if="property.data_type === 'date'"
          :label="property.name"
          :hint="getHint(property)"
          :error="validateField(property, getFieldValue(property))"
        >
          <UIDatePicker
            :model-value="getDateFieldValue(property)"
            :disabled="props.loading"
            @update:model-value="updateFieldValue(property, $event)"
          />
        </UiField>

        <!-- Boolean field -->
        <div v-else-if="property.data_type === 'boolean'" class="boolean-field">
          <div class="switch-container">
            <UiSwitch
              :model-value="getBooleanFieldValue(property)"
              :disabled="props.loading"
              @update:model-value="updateFieldValue(property, $event)"
            />
            <label class="switch-label">{{ property.name }}</label>
          </div>
          <span v-if="property.description" class="field-hint">{{
            property.description
          }}</span>
        </div>

        <!-- Select field -->
        <UiSelect
          v-else-if="property.data_type === 'select'"
          :model-value="getSelectFieldValue(property)"
          :name="property.key"
          :label="property.name"
          :options="getSelectOptions(property)"
          :hint="getHint(property)"
          :required="isFieldRequired(property)"
          :disabled="props.loading"
          :error="validateField(property, getFieldValue(property))"
          @update:model-value="updateFieldValue(property, $event)"
        />

        <!-- Multi-select field -->
        <div
          v-else-if="property.data_type === 'multiselect'"
          class="multiselect-field"
        >
          <label>{{ property.name }}</label>
          <UiMultiSelect
            :model-value="getMultiSelectFieldValue(property)"
            :options="getSelectOptions(property)"
            :placeholder="$t('form.pleaseSelect')"
            :disabled="props.loading"
            @update:model-value="updateFieldValue(property, $event)"
          />
          <p v-if="getHint(property)">{{ getHint(property) }}</p>
          <p v-if="validateField(property, getFieldValue(property))">
            {{ validateField(property, getFieldValue(property)) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.project-custom-properties {
  .loading {
    display: flex;
    justify-content: center;
    padding: var(--space-24);
  }

  .no-properties {
    // Empty state - no custom properties defined
    display: none;
  }

  .properties-fields {
    display: flex;
    flex-direction: column;
    gap: var(--space-16);

    .property-field {
      :deep(.dp__input_icon_pad) {
        margin-right: 0;
      }

      .multiselect-field {
        > label {
          display: block;
          flex-grow: 1;
          height: 20px;
          font-style: normal;
          line-height: var(--line-height-20);
          font-weight: var(--font-weight-medium);
          color: var(--color-gray-700);
          margin-bottom: var(--space-6);
        }
      }

      .boolean-field {
        display: flex;
        flex-direction: column;
        gap: var(--space-8);

        .switch-container {
          display: flex;
          align-items: center;

          .switch-label {
            margin-left: var(--space-12);
            font-size: var(--font-size-14);
            font-weight: var(--font-weight-medium);
            color: var(--color-gray-700);
          }
        }

        .field-hint {
          font-size: var(--font-size-12);
          color: var(--color-gray-500);
          margin-left: var(--space-40); // Align with switch toggle
        }
      }
    }
  }
}
</style>
